import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { addCustomPage, getCustomPages, updateCustomPage } from "api";

import Loading from "Manage/Loading";
import ShortTextField from "Manage/fields/ShortTextField";
import LongTextField from "Manage/fields/LongTextField";

const EditPage = () => {
  const { id: pageId } = useParams();
  const history = useHistory();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [content, setContent] = useState("");

  const [message, setMessage] = useState();
  const [loadError, setLoadError] = useState();
  const [isLoading, setIsLoading] = useState(Boolean(pageId));

  const handleContentChange = (value) => {
    const nextContent =
      typeof value === "string" ? value : value?.target?.innerHTML;

    if (typeof nextContent === "string") {
      setContent(nextContent);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadPage = async () => {
      if (!pageId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError();

      try {
        const pages = await getCustomPages();
        const page = Array.isArray(pages)
          ? pages.find(({ _id }) => String(_id) === pageId)
          : undefined;

        if (!page) {
          if (isMounted) {
            setLoadError("Page not found.");
          }
          return;
        }

        if (isMounted) {
          setTitle(page.title || "");
          setSlug(page.slug || "");
          setContent(typeof page.content === "string" ? page.content : "");
        }
      } catch (error) {
        if (isMounted) {
          setLoadError("Unable to load the page.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadPage();

    return () => {
      isMounted = false;
    };
  }, [pageId]);

  const handleSave = async (e) => {
    e.preventDefault();
    const page = {
      title,
      slug,
      content,
    };
    if (pageId) {
      // Editing
      const updatedPage = await updateCustomPage(page, pageId);
      if (updatedPage.success) {
        history.push(`/p/${slug}`);
      } else if (updatedPage.error) {
        setMessage(updatedPage.error);
      } else {
        setMessage("An error occurred.");
      }
    } else {
      // Add new
      const newPage = await addCustomPage(page);
      if (newPage._id) {
        history.push(`/p/${newPage.slug || newPage._id}`);
      } else if (newPage.error) {
        setMessage(newPage.error);
      } else {
        setMessage("An error occurred.");
      }
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (loadError) {
    return (
      <div id="main-content-wrapper">
        <div
          className="container"
          style={{ paddingTop: 100, paddingBottom: 100 }}
        >
          <div role="alert" className="alert alert-warning">
            <p>{loadError}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="main-content-wrapper">
      <div
        className="container"
        style={{ paddingTop: 100, paddingBottom: 100 }}
      >
        <form className="col-md-12 form-horizontal" onSubmit={handleSave}>
          <h3>{pageId ? "Edit" : "Add"} page</h3>
          <hr />
          {message && (
            <div role="alert" className={"alert alert-warning"}>
              <p>{message}</p>
            </div>
          )}
          <div className="form-group">
            <div className="col-sm-2">
              <label htmlFor="title" className="control-label">
                Title
              </label>
            </div>
            <ShortTextField
              name="title"
              value={title}
              setValue={setTitle}
            />
          </div>
          <div className="form-group">
            <div className="col-sm-2">
              <label htmlFor="slug" className="control-label">
                Slug
              </label>
            </div>
            <ShortTextField
              name="slug"
              value={slug}
              setValue={setSlug}
              placeholder={
                "url-name-with-no-spaces (auto generated if left blank)"
              }
            />
          </div>
          <div className="form-group">
            <div className="col-sm-2">
              <label htmlFor="content" className="control-label">
                Content
              </label>
            </div>
            <LongTextField
              name="content"
              value={content}
              setValue={handleContentChange}
            />
          </div>
          <hr />
          <div className="form-group">
            <div className="col-sm-2" />
            <div className="col-sm-10">
              <button className="btn btn-primary" type="submit">
                Save Page
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPage;
